import { createPortal } from "react-dom";
import { useEffect, useState, useCallback, useRef, useMemo, cloneElement, isValidElement } from "react";
import { useMsal } from "@azure/msal-react";
import { FolderPlus, Folder, Pencil } from "lucide-react";
import { callApi } from "../api/api";
import { notifyAuthContextChanged } from "../utils/authCache";
import { useServerDuplicateCheck } from "../hooks/useServerDuplicateCheck";
import RBAC from "../components/RBAC";
import ResponsiveTableCards from "../components/ResponsiveTableCards";
import ScrollableTable from "../components/ScrollableTable";
import Pagination from "../components/Pagination";
import LoadingOverlay from "../components/LoadingOverlay";
import ManagePageHeader from "../components/ManagePageHeader";

const RESOURCE_CODE_PATTERN = /^[A-Z0-9_]+$/;
const RESOURCE_NAME_PATTERN = /^[a-zA-Z0-9\s\-'&,()]+$/;
const RESOURCE_TYPE_LOOKUP_CATEGORY = "resource_type";
const DOCUMENT_TYPE_LOOKUP_CATEGORY = "document_type";
const ADLS_DATA_ACCESS_TYPE = "ADLS_DATA_ACCESS";
const PORTAL_FEATURE_TYPE = "PORTAL_FEATURE";
// TEMPORARY_PORTAL_FEATURE_ADD: keep until the user asks to remove portal-feature create.

const normalizeResourceCode = (value) => (value || "").trim().toUpperCase();

const nameToResourceCode = (value) =>
  normalizeResourceCode(
    (value || "")
      .trim()
      .replace(/[^a-zA-Z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .replace(/_+/g, "_"),
  );

const isResourceActive = (resource) =>
  resource?.is_active !== false && resource?.is_active !== 0;

const isPortalFeatureResource = (resource) =>
  (resource?.resource_type_code || "")
    .trim()
    .toUpperCase()
    .replace(/-/g, "_") === PORTAL_FEATURE_TYPE;

const isAdlsDataAccessResource = (resource) =>
  (resource?.resource_type_code || "")
    .trim()
    .toUpperCase()
    .replace(/-/g, "_") === "ADLS_DATA_ACCESS";

const isInactiveResource = (resource) => !isResourceActive(resource);

const isToggleableResource = (resource) =>
  isPortalFeatureResource(resource) || isAdlsDataAccessResource(resource);

const getResourceToggleAriaLabel = (resource) => {
  const action = isResourceActive(resource) ? "Disable" : "Enable";

  if (isPortalFeatureResource(resource)) {
    return `${action} ${resource.resource_name} in navigation`;
  }

  if (isAdlsDataAccessResource(resource)) {
    return `${action} ${resource.resource_name} for role assignment`;
  }

  return `${action} ${resource.resource_name}`;
};

const getResourceToggleTooltip = (resource) =>
  isResourceActive(resource) ? "Disable Resource" : "Enable Resource";

const getResourceDeactivateMessage = (resource) => {
  if (isPortalFeatureResource(resource)) {
    return "This portal feature will be disabled in navigation until it is enabled again.";
  }

  if (isAdlsDataAccessResource(resource)) {
    return "This resource will become inactive in role assignment until it is enabled again.";
  }

  return "This resource will be deactivated until it is enabled again.";
};

const RESOURCE_CODE_HINT =
  "Required. Use letters, numbers, and underscores only (e.g. SEARCH_DOC).";

const RESOURCE_CODE_TITLE =
  "Unique resource code. Allowed characters: A-Z, 0-9, and underscore (_).";

const RESOURCE_NAME_HINT =
  "Use letters, numbers, spaces, and - ' & , ( ). Slashes and symbols are not allowed.";

const RESOURCE_NAME_TITLE =
  "Display name for the resource. Allowed: letters, numbers, spaces, and - ' & , ( ).";

export default function Resources() {
  const { instance, accounts } = useMsal();


  const [resources, setResources] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  const [selected, setSelected] = useState(null);

  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDeactivate, setShowDeactivate] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const [resourceTypes, setResourceTypes] = useState([]);
  const [resourceTypesLoading, setResourceTypesLoading] = useState(false);
  const resourceTypeControllerRef = useRef(null);
  const codeManuallyEditedRef = useRef(false);

  const [documentTypes, setDocumentTypes] = useState([]);
  const [documentTypesLoading, setDocumentTypesLoading] = useState(false);
  const documentTypeControllerRef = useRef(null);

  const [form, setForm] = useState({
    resource_code: "",
    resource_name: "",
    resource_type_code: "",
    resource_path: "",
    access_level_code: "READ",
  });

  /* ================= SEARCH DEBOUNCE ================= */

  const [isNarrowToolbar, setIsNarrowToolbar] = useState(false);
  const [isAddTooltipVisible, setIsAddTooltipVisible] = useState(false);
  const addTooltipTouchTimer = useRef(null);

  useEffect(() => {
    return () => {
      if (addTooltipTouchTimer.current) {
        clearTimeout(addTooltipTouchTimer.current);
      }

      if (resourceTypeControllerRef.current) {
        resourceTypeControllerRef.current.abort();
      }

      if (documentTypeControllerRef.current) {
        documentTypeControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    const update = () => setIsNarrowToolbar(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!showEdit || !accounts.length) {
      return undefined;
    }

    if (resourceTypeControllerRef.current) {
      resourceTypeControllerRef.current.abort();
    }

    const controller = new AbortController();
    resourceTypeControllerRef.current = controller;

    const loadResourceTypes = async () => {
      try {
        setResourceTypesLoading(true);

        const res = await callApi(
          instance,
          accounts,
          "GET",
          `/api/admin/lookup/${RESOURCE_TYPE_LOOKUP_CATEGORY}`,
          null,
          { signal: controller.signal },
        );

        if (controller.signal.aborted) {
          return;
        }

        setResourceTypes(res?.data?.data || []);
      } catch (err) {
        if (
          err?.name === "AbortError" ||
          err?.message === "Request cancelled"
        ) {
          return;
        }

        console.error("Resource type lookup load failed", err);
        setResourceTypes([]);
      } finally {
        if (!controller.signal.aborted) {
          setResourceTypesLoading(false);
        }
      }
    };

    loadResourceTypes();

    return () => {
      controller.abort();
    };
  }, [showEdit, accounts.length, instance, accounts]);

  useEffect(() => {
    if (!showAdd || !accounts.length) {
      return undefined;
    }

    if (documentTypeControllerRef.current) {
      documentTypeControllerRef.current.abort();
    }

    const controller = new AbortController();
    documentTypeControllerRef.current = controller;

    const loadDocumentTypes = async () => {
      try {
        setDocumentTypesLoading(true);

        const res = await callApi(
          instance,
          accounts,
          "GET",
          `/api/admin/lookup/${DOCUMENT_TYPE_LOOKUP_CATEGORY}`,
          null,
          { signal: controller.signal },
        );

        if (controller.signal.aborted) {
          return;
        }

        setDocumentTypes(res?.data?.data || []);
      } catch (err) {
        if (
          err?.name === "AbortError" ||
          err?.message === "Request cancelled"
        ) {
          return;
        }

        console.error("Document type lookup load failed", err);
        setDocumentTypes([]);
      } finally {
        if (!controller.signal.aborted) {
          setDocumentTypesLoading(false);
        }
      }
    };

    loadDocumentTypes();

    return () => {
      controller.abort();
    };
  }, [showAdd, accounts.length, instance, accounts]);

  useEffect(() => {
    if (!showEdit || !resourceTypes.length || !form.resource_type_code) {
      return;
    }

    const matched = resourceTypes.find(
      (item) =>
        item.lookup_code?.toLowerCase() ===
        form.resource_type_code.toLowerCase(),
    );

    if (matched && matched.lookup_code !== form.resource_type_code) {
      setForm((prev) => ({
        ...prev,
        resource_type_code: matched.lookup_code,
      }));
    }
  }, [showEdit, resourceTypes, form.resource_type_code]);

  /* ================= LOAD ================= */

  const loadResources = useCallback(
    async (searchText = "", currentPage = 1) => {
      try {
        setLoading(true);

        const finalSearch =
          searchText !== undefined && searchText !== null ? searchText : "";

        const res = await callApi(
          instance,
          accounts,
          "GET",
          `/api/admin/resources?page=${currentPage}&pageSize=${pageSize}&search=${encodeURIComponent(finalSearch)}&_=${Date.now()}`,
        );

        const response = res?.data || {};

        setResources(response.data || []);
        setTotalRecords(response.total || 0);
      } catch (err) {
        console.error(err);

        setResources([]);
        setTotalRecords(0);
      } finally {
        setLoading(false);
      }
    },
    [instance, accounts, pageSize],
  );

  useEffect(() => {
    if (accounts.length) {
      loadResources(debouncedSearch, page);
    }
  }, [accounts.length, debouncedSearch, page, loadResources]);

  /* ================= HELPERS ================= */

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });

    setTimeout(() => setToast(null), 3000);
  };

  const resetForm = () => {
    setForm({
      resource_code: "",
      resource_name: "",
      resource_type_code: "",
      resource_path: "",
      access_level_code: "READ",
    });

    setSubmitAttempted(false);
  };

  const validationExcludeResourceId =
    showEdit && selected ? selected.resource_id : null;

  const duplicateCheckParams = useMemo(() => {
    const params = {};
    const name = form.resource_name?.trim();
    const code = normalizeResourceCode(form.resource_code);

    if (name && RESOURCE_NAME_PATTERN.test(name)) {
      params.name = name;
    }

    if (code && RESOURCE_CODE_PATTERN.test(code)) {
      params.code = code;
    }

    if (validationExcludeResourceId) {
      params.excludeId = validationExcludeResourceId;
    }

    return params;
  }, [
    form.resource_name,
    form.resource_code,
    validationExcludeResourceId,
  ]);

  const duplicateCheckEnabled =
    (showAdd || showEdit) &&
    !!accounts.length &&
    (!!duplicateCheckParams.name || !!duplicateCheckParams.code);

  const { codeTaken, nameTaken, checking: duplicateChecking } =
    useServerDuplicateCheck({
      enabled: duplicateCheckEnabled,
      instance,
      accounts,
      url: "/api/admin/resources/check-duplicate",
      params: duplicateCheckParams,
    });

  const getResourceCodeError = () => {
    const raw = form.resource_code || "";
    const code = normalizeResourceCode(raw);

    if (raw && !RESOURCE_CODE_PATTERN.test(code)) {
      return "Code must use letters, numbers, and underscores only";
    }

    if (submitAttempted && !code) {
      return "Code required";
    }

    if (code && codeTaken) {
      return "A resource with this code already exists";
    }

    return null;
  };

  const getResourceNameError = () => {
    const raw = form.resource_name || "";
    const name = raw.trim();

    if (raw && !RESOURCE_NAME_PATTERN.test(name)) {
      return "Name must use letters, numbers, spaces, and - ' & , ( ) only";
    }

    if (submitAttempted && !name) {
      return "Name required";
    }

    if (name && nameTaken) {
      return "A resource with this name already exists";
    }

    return null;
  };

  const documentTypeOptions = useMemo(
    () =>
      documentTypes.map((item) => ({
        value: item.lookup_code,
        label: item.lookup_name || item.lookup_code,
      })),
    [documentTypes],
  );

  const isAddingPortalFeature =
    showAdd &&
    (form.resource_type_code || "")
      .trim()
      .toUpperCase()
      .replace(/-/g, "_") === PORTAL_FEATURE_TYPE;

  const getResourcePathError = () => {
    if (showAdd) {
      if (submitAttempted && !form.resource_path?.trim()) {
        return isAddingPortalFeature ? "Path required" : "Document type required";
      }

      return null;
    }

    if (submitAttempted && !form.resource_path?.trim()) {
      return "Path required";
    }

    return null;
  };

  const hasAddPath = () =>
    isAddingPortalFeature
      ? !!form.resource_path?.trim()
      : !!normalizeResourceCode(form.resource_path);

  const validateAdd = () => {
    setSubmitAttempted(true);

    const code = normalizeResourceCode(form.resource_code);
    const name = form.resource_name?.trim();

    return (
      !!code &&
      RESOURCE_CODE_PATTERN.test(code) &&
      !!name &&
      RESOURCE_NAME_PATTERN.test(name) &&
      hasAddPath() &&
      !nameTaken &&
      !codeTaken &&
      !duplicateChecking
    );
  };

  const validateEdit = () => {
    setSubmitAttempted(true);

    const code = normalizeResourceCode(form.resource_code);
    const name = form.resource_name?.trim();

    return (
      !!code &&
      RESOURCE_CODE_PATTERN.test(code) &&
      !!name &&
      RESOURCE_NAME_PATTERN.test(name) &&
      !!form.resource_path?.trim() &&
      !nameTaken &&
      !codeTaken &&
      !duplicateChecking
    );
  };

  const canSubmitResourceForm = useMemo(() => {
    const code = normalizeResourceCode(form.resource_code);
    const name = form.resource_name?.trim();

    if (!name || !code) {
      return false;
    }

    if (!RESOURCE_CODE_PATTERN.test(code)) {
      return false;
    }

    if (!RESOURCE_NAME_PATTERN.test(name)) {
      return false;
    }

    if (nameTaken || codeTaken || duplicateChecking) {
      return false;
    }

    if (showAdd) {
      return isAddingPortalFeature
        ? !!form.resource_path?.trim()
        : !!normalizeResourceCode(form.resource_path);
    }

    if (showEdit) {
      return !!form.resource_path?.trim();
    }

    return false;
  }, [
    showAdd,
    showEdit,
    isAddingPortalFeature,
    form.resource_name,
    form.resource_code,
    form.resource_path,
    nameTaken,
    codeTaken,
    duplicateChecking,
  ]);

  const buildAddPayload = () => {
    if (isAddingPortalFeature) {
      return {
        resource_code: normalizeResourceCode(form.resource_code),
        resource_name: form.resource_name.trim(),
        resource_type_code: PORTAL_FEATURE_TYPE,
        resource_path: form.resource_path.trim(),
        access_level_code: form.access_level_code,
      };
    }

    return {
      resource_code: normalizeResourceCode(form.resource_code),
      resource_name: form.resource_name.trim(),
      resource_type_code: ADLS_DATA_ACCESS_TYPE,
      resource_path: normalizeResourceCode(form.resource_path),
      access_level_code: form.access_level_code,
    };
  };

  const cleanPayload = () => ({
    resource_code: normalizeResourceCode(form.resource_code),
    resource_name: form.resource_name.trim(),
    resource_type_code: form.resource_type_code,
    resource_path: form.resource_path.trim(),
    access_level_code: form.access_level_code,
  });

  const editResourceTypeOptions = useMemo(() => {
    const currentType = form.resource_type_code?.trim();

    if (!currentType) {
      return [];
    }

    const matchedLookup = resourceTypes.find(
      (item) =>
        (item.lookup_code || "").trim().toLowerCase() ===
        currentType.toLowerCase(),
    );

    if (matchedLookup) {
      return [
        {
          value: matchedLookup.lookup_code,
          label: matchedLookup.lookup_name || matchedLookup.lookup_code,
        },
      ];
    }

    return [{ value: currentType, label: currentType }];
  }, [resourceTypes, form.resource_type_code]);

  /* ================= ACTIONS ================= */

  const handleAdd = async () => {
    if (!validateAdd()) return;

    try {
      setSubmitting(true);

      const res = await callApi(
        instance,
        accounts,
        "POST",
        "/api/admin/resources",
        buildAddPayload(),
      );

      if (!res || res?.data?.success === false) {
        throw new Error(res?.data?.message || "Create failed");
      }

      showToast("Resource created");

      if (isAddingPortalFeature) {
        notifyAuthContextChanged();
      }

      setShowAdd(false);

      resetForm();

      loadResources(debouncedSearch, page);
    } catch (err) {
      showToast(
        err?.response?.data?.message || err.message || "Create failed",
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async () => {
    if (!selected) return;
    if (!validateEdit()) return;

    try {
      setSubmitting(true);

      const res = await callApi(
        instance,
        accounts,
        "PUT",
        `/api/admin/resources/${selected.resource_id}`,
        cleanPayload(),
      );

      if (!res || res?.data?.success === false) {
        throw new Error(res?.data?.message || "Update failed");
      }

      showToast("Resource updated");

      setShowEdit(false);

      setSelected(null);

      resetForm();

      loadResources(debouncedSearch, page);
    } catch (err) {
      showToast(
        err?.response?.data?.message || err.message || "Update failed",
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const openDeactivateModal = useCallback((resource) => {
    setSelected(resource);
    setShowDeactivate(true);
  }, []);

  const closeDeactivateModal = useCallback(() => {
    setShowDeactivate(false);
  }, []);

  const updateResourceInList = useCallback((resourceId, changes) => {
    setResources((prev) =>
      prev.map((resource) =>
        resource.resource_id === resourceId
          ? { ...resource, ...changes }
          : resource,
      ),
    );
  }, []);

  const confirmDeactivate = useCallback(async () => {
    if (!selected) return;

    const resourceId = selected.resource_id;

    try {
      setSubmitting(true);

      const res = await callApi(
        instance,
        accounts,
        "DELETE",
        `/api/admin/resources/${resourceId}`,
      );

      if (!res || res?.data?.success === false) {
        throw new Error(res?.data?.message || "Disable failed");
      }

      updateResourceInList(resourceId, { is_active: 0 });
      setSelected((prev) =>
        prev?.resource_id === resourceId ? { ...prev, is_active: 0 } : prev,
      );
      showToast("Resource updated");
      closeDeactivateModal();
      notifyAuthContextChanged();
    } catch (err) {
      showToast(
        err?.response?.data?.message || err.message || "Disable failed",
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  }, [
    instance,
    accounts,
    selected,
    updateResourceInList,
    closeDeactivateModal,
  ]);

  const handleActivate = useCallback(
    async (resource) => {
      try {
        setSubmitting(true);

        const res = await callApi(
          instance,
          accounts,
          "PUT",
          `/api/admin/resources/${resource.resource_id}/activate`,
        );

        if (!res || res?.data?.success === false) {
          throw new Error(res?.data?.message || "Activate failed");
        }

        updateResourceInList(resource.resource_id, { is_active: 1 });
        setSelected((prev) =>
          prev?.resource_id === resource.resource_id
            ? { ...prev, is_active: 1 }
            : prev,
        );
        showToast("Resource updated");
        notifyAuthContextChanged();
      } catch (err) {
        showToast(
          err?.response?.data?.message || err.message || "Activate failed",
          "error",
        );
      } finally {
        setSubmitting(false);
      }
    },
    [instance, accounts, updateResourceInList],
  );

  const handleResourceStatusToggle = useCallback(
    (resource, enabled) => {
      if (enabled) {
        handleActivate(resource);
        return;
      }

      openDeactivateModal(resource);
    },
    [handleActivate, openDeactivateModal],
  );

  const openAddResourceModal = () => {
    codeManuallyEditedRef.current = false;
    resetForm();
    setForm((prev) => ({
      ...prev,
      resource_type_code: ADLS_DATA_ACCESS_TYPE,
    }));
    setShowAdd(true);
  };

  const openEditResourceModal = useCallback((resource) => {
    setSelected(resource);
    setForm({
      resource_code: resource.resource_code,
      resource_name: resource.resource_name,
      resource_type_code: resource.resource_type_code || "",
      resource_path: resource.resource_path,
      access_level_code: resource.access_level_code,
    });
    setSubmitAttempted(false);
    setShowEdit(true);
  }, []);

  const showAddTooltip = () => setIsAddTooltipVisible(true);

  const hideAddTooltip = () => setIsAddTooltipVisible(false);

  const handleAddTooltipTouchStart = () => {
    if (addTooltipTouchTimer.current) {
      clearTimeout(addTooltipTouchTimer.current);
    }
    setIsAddTooltipVisible(true);
  };

  const handleAddTooltipTouchEnd = () => {
    addTooltipTouchTimer.current = setTimeout(() => {
      setIsAddTooltipVisible(false);
    }, 1200);
  };

  /* ================= RESPONSIVE CARD MAPPERS ================= */

  const mapResourceCard = useCallback(
    (r) => ({
      id: `resource-${r.resource_id}`,
      icon: <Folder size={16} />,
      tone: isResourceActive(r)
        ? r.access_level_code === "WRITE"
          ? "success"
          : "info"
        : "warning",
      title: r.resource_name,
      subtitle: r.resource_code || "—",
      meta: [
        { label: "Code", value: r.resource_code || "—" },
        { label: "Type", value: r.resource_type_code || "—" },
        { label: "Access", value: r.access_level_code || "—" },
        { label: "Path", value: r.resource_path || "—" },
      ],
      badges: [
        {
          label: "Access",
          value: r.access_level_code || "—",
          tone: r.access_level_code === "WRITE" ? "success" : "info",
        },
      ],
      actions: [
        {
          label: "Edit",
          icon: <Pencil size={14} />,
          onClick: () => openEditResourceModal(r),
        },
      ],
    }),
    [openEditResourceModal],
  );

  const mapResourceDrawer = useCallback(
    (r) => ({
      title: r.resource_name,
      fields: [
        { label: "Resource Code", value: r.resource_code || "—" },
        { label: "Type Code", value: r.resource_type_code || "—" },
        { label: "Access Level", value: r.access_level_code || "—" },
        { label: "Path", value: r.resource_path || "—" },
      ],
      primaryAction: {
        label: "Edit Resource",
        onClick: () => openEditResourceModal(r),
      },
    }),
    [openEditResourceModal],
  );

  const renderTable = () => (
    <>
      <div
        style={{
          ...styles.tableCard,
          position: "relative",
          minHeight: loading && resources.length === 0 ? 180 : undefined,
        }}
      >
        <LoadingOverlay show={loading} label="Loading..." />
        <ScrollableTable style={loading ? { opacity: 0.45 } : undefined}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>NAME</th>

                <th style={styles.th}>CODE</th>

                <th style={styles.th}>TYPE</th>

                <th style={styles.th}>ACCESS</th>

                <th style={styles.th}>PATH</th>

                <th style={styles.thRight}>ACTIONS</th>
              </tr>
            </thead>

            <tbody>
              {!loading && resources.length === 0 ? (
                <tr>
                  <td
                    colSpan="6"
                    style={{
                      padding: "32px",
                      textAlign: "center",
                      color: "#64748b",
                      fontSize: "14px",
                    }}
                  >
                    No records found
                  </td>
                </tr>
              ) : (
                resources.map((r) => {
                  const rowMuted = isInactiveResource(r);

                  return (
                  <tr
                    key={r.resource_id}
                    style={{
                      ...styles.row,
                      ...(rowMuted ? styles.disabledPortalFeatureRow : null),
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = rowMuted
                        ? "#e2e8f0"
                        : "#f8fafc";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = rowMuted
                        ? "#f1f5f9"
                        : "#fff";
                    }}
                  >
                    <td style={{ ...styles.td, ...(rowMuted ? styles.tdMuted : null) }}>
                      <div
                        style={{
                          fontWeight: "600",
                          ...(rowMuted ? styles.nameMuted : null),
                        }}
                      >
                        {r.resource_name}
                      </div>
                    </td>

                    <td style={{ ...styles.td, ...(rowMuted ? styles.tdMuted : null) }}>
                      <span
                        style={{
                          ...styles.codeBadge,
                          ...(rowMuted ? styles.badgeMuted : null),
                        }}
                      >
                        {r.resource_code || "—"}
                      </span>
                    </td>

                    <td style={{ ...styles.td, ...(rowMuted ? styles.tdMuted : null) }}>
                      <span
                        style={{
                          ...styles.typeBadge,
                          ...(rowMuted ? styles.badgeMuted : null),
                        }}
                      >
                        {r.resource_type_code || "—"}
                      </span>
                    </td>

                    <td style={{ ...styles.td, ...(rowMuted ? styles.tdMuted : null) }}>
                      <span
                        style={{
                          ...(r.access_level_code === "WRITE"
                            ? styles.accessWrite
                            : styles.accessRead),
                          ...(rowMuted ? styles.badgeMuted : null),
                        }}
                      >
                        {r.access_level_code}
                      </span>
                    </td>

                    <td
                      style={{
                        ...styles.td,
                        ...(rowMuted ? styles.tdMuted : null),
                        minWidth: "320px",
                      }}
                    >
                      {r.resource_path || "—"}
                    </td>

                    <td style={styles.tdRight}>
                      <RBAC resource="ADMIN" access="WRITE">
                        <ActionTooltip text="Edit">
                          <button
                            type="button"
                            style={styles.iconAction}
                            aria-label={`Edit ${r.resource_name}`}
                            disabled={submitting}
                            onClick={() => openEditResourceModal(r)}
                          >
                            ✏️
                          </button>
                        </ActionTooltip>
                      </RBAC>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
          </ScrollableTable>
      </div>

      {!loading && totalRecords > 0 && (
        <Pagination
          page={page}
          totalPages={Math.ceil(totalRecords / pageSize) || 1}
          totalRecords={totalRecords}
          pageSize={pageSize}
          currentPageCount={resources.length}
          onPageChange={setPage}
        />
      )}
    </>
  );

  /* ================= UI ================= */

  const paginationProps =
    totalRecords > 0
      ? {
          page,
          totalPages: Math.ceil(totalRecords / pageSize) || 1,
          totalRecords,
          pageSize,
          currentPageCount: resources.length,
          onPageChange: setPage,
        }
      : null;

  const editFormLocked =
    showEdit && selected ? !isResourceActive(selected) : false;

  const editModalHeaderActions =
    showEdit && selected && isToggleableResource(selected) ? (
      <StatusToggle
        checked={isResourceActive(selected)}
        disabled={submitting}
        ariaLabel={getResourceToggleAriaLabel(selected)}
        tooltipText={getResourceToggleTooltip(selected)}
        onChange={(enabled) => handleResourceStatusToggle(selected, enabled)}
      />
    ) : null;

  return (
    <div className="page-container" style={styles.container}>
      <ManagePageHeader pageTitle="Resources" />
      <div
        style={{
          ...styles.header,
          ...(isNarrowToolbar ? styles.headerNarrow : null),
        }}
      >
        <div
          style={isNarrowToolbar ? styles.headerTitleBlock : undefined}
        >
          <h2 style={styles.title}>Resources</h2>

          <p style={styles.sub}>Manage RBAC resources</p>
        </div>

        <RBAC resource="ADMIN" access="WRITE">
          {isNarrowToolbar ? (
            <div style={styles.addBtnWrap}>
              <button
                type="button"
                style={{
                  ...styles.addBtn,
                  ...styles.addBtnCompact,
                }}
                aria-label="Add resource"
                aria-describedby={
                  isAddTooltipVisible ? "resources-add-tooltip" : undefined
                }
                onMouseEnter={showAddTooltip}
                onMouseLeave={hideAddTooltip}
                onFocus={showAddTooltip}
                onBlur={hideAddTooltip}
                onTouchStart={handleAddTooltipTouchStart}
                onTouchEnd={handleAddTooltipTouchEnd}
                onClick={openAddResourceModal}
              >
                <FolderPlus size={20} strokeWidth={2} aria-hidden />
              </button>
              {isAddTooltipVisible && (
                <div
                  id="resources-add-tooltip"
                  style={styles.addTooltip}
                  role="tooltip"
                >
                  <span style={styles.addTooltipArrow} aria-hidden />
                  Add resource
                </div>
              )}
            </div>
          ) : (
            <button
              type="button"
              style={styles.addBtn}
              onClick={openAddResourceModal}
            >
              + Add resource
            </button>
          )}
        </RBAC>
      </div>

      <div
        style={{
          ...styles.toolbar,
          ...(isNarrowToolbar ? styles.toolbarNarrow : null),
        }}
      >
        <input
          placeholder="Search resources"
          style={{
            ...styles.search,
            ...(isNarrowToolbar ? styles.searchNarrow : null),
          }}
          value={search}
          onFocus={(e) => (e.target.style.border = "1px solid #2563eb")}
          onBlur={(e) => (e.target.style.border = "1px solid #dbe2ea")}
          onChange={(e) => {
            setPage(1);
            setSearch(e.target.value);
          }}
        />

      </div>

      <ResponsiveTableCards
        breakpoint={1024}
        data={resources}
        loading={loading}
        loadingLabel="Loading..."
        emptyTitle="No Resources Found"
        emptySub="No resources match your current search."
        listTitle="Resources"
        listSubtitle="Manage RBAC resources"
        cardMapper={mapResourceCard}
        drawerMapper={mapResourceDrawer}
        pagination={paginationProps || undefined}
        renderTable={renderTable}
      />

      {(showAdd || showEdit) && (
        <Modal
          title={showAdd ? "Add Resource" : "Edit Resource"}
          headerActions={editModalHeaderActions}
          onClose={() => {
            setShowAdd(false);
            setShowEdit(false);
            setSelected(null);
            resetForm();
          }}
        >
          {showAdd && (
            <div style={styles.field}>
              <label htmlFor="add-resource-type" style={styles.label}>
                Resource type
              </label>
              {/* TEMPORARY_PORTAL_FEATURE_ADD */}
              <select
                id="add-resource-type"
                style={styles.input}
                value={form.resource_type_code || ADLS_DATA_ACCESS_TYPE}
                onChange={(e) => {
                  setSubmitAttempted(false);
                  setForm((prev) => ({
                    ...prev,
                    resource_type_code: e.target.value,
                    resource_path: "",
                  }));
                }}
              >
                <option value={ADLS_DATA_ACCESS_TYPE}>
                  {ADLS_DATA_ACCESS_TYPE}
                </option>
                <option value={PORTAL_FEATURE_TYPE}>
                  {PORTAL_FEATURE_TYPE}
                </option>
              </select>
            </div>
          )}

          <FormField
            label="Name"
            required
            hint={RESOURCE_NAME_HINT}
            hintOnHover
            error={getResourceNameError()}
          >
            <Input
              value={form.resource_name}
              title={RESOURCE_NAME_TITLE}
              invalid={!!getResourceNameError()}
              disabled={editFormLocked}
              onChange={(v) => {
                if (showAdd && !codeManuallyEditedRef.current) {
                  setForm({
                    ...form,
                    resource_name: v,
                    resource_code: nameToResourceCode(v),
                  });
                  return;
                }

                setForm({ ...form, resource_name: v });
              }}
              onBlur={() => {
                if (
                  showAdd &&
                  !codeManuallyEditedRef.current &&
                  form.resource_name?.trim()
                ) {
                  setForm((prev) => ({
                    ...prev,
                    resource_code: nameToResourceCode(prev.resource_name),
                  }));
                }
              }}
            />
          </FormField>

          <FormField
            label="Code"
            required
            hint={RESOURCE_CODE_HINT}
            hintOnHover
            error={getResourceCodeError()}
          >
            <Input
              value={form.resource_code}
              placeholder="e.g. SEARCH_DOC"
              title={RESOURCE_CODE_TITLE}
              invalid={!!getResourceCodeError()}
              disabled={editFormLocked}
              onChange={(v) => {
                if (showAdd) {
                  codeManuallyEditedRef.current = true;
                }

                setForm({ ...form, resource_code: v });
              }}
            />
          </FormField>

          {showAdd ? (
            isAddingPortalFeature ? (
              <div style={styles.field}>
                <label htmlFor="add-portal-feature-type" style={styles.label}>
                  Type
                </label>
                <Input
                  id="add-portal-feature-type"
                  value={PORTAL_FEATURE_TYPE}
                  disabled
                  title="This resource will be created as a portal feature"
                  onChange={() => {}}
                />
              </div>
            ) : (
            <div style={styles.field}>
              <label htmlFor="document-type-code" style={styles.label}>
                Document Type
              </label>

              <select
                id="document-type-code"
                style={{
                  ...styles.input,
                  ...(getResourcePathError() ? styles.inputInvalid : null),
                }}
                value={form.resource_path}
                disabled={documentTypesLoading}
                onChange={(e) =>
                  setForm({
                    ...form,
                    resource_path: e.target.value,
                  })
                }
              >
                <option value="">
                  {documentTypesLoading
                    ? "Loading document types..."
                    : "Select document type"}
                </option>

                {documentTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>

              {getResourcePathError() && (
                <Error msg={getResourcePathError()} />
              )}
            </div>
            )
          ) : (
            <div style={styles.field}>
              <label htmlFor="resource-type-code" style={styles.label}>
                Type
              </label>

              <select
                id="resource-type-code"
                style={styles.input}
                value={form.resource_type_code}
                disabled
              >
                {editResourceTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div style={styles.field}>
            <label style={styles.label}>Access Level</label>

            <select
              style={{
                ...styles.input,
                ...(editFormLocked ? styles.inputDisabled : null),
              }}
              value={form.access_level_code}
              disabled={editFormLocked}
              onChange={(e) =>
                setForm({
                  ...form,
                  access_level_code: e.target.value,
                })
              }
            >
              <option value="READ">READ</option>

              <option value="WRITE">WRITE</option>
            </select>
          </div>

          <FormField
            label="Path"
            required={showEdit || isAddingPortalFeature}
            error={
              showEdit || isAddingPortalFeature ? getResourcePathError() : null
            }
          >
            <Input
              value={form.resource_path}
              disabled={
                (showAdd && !isAddingPortalFeature) || editFormLocked
              }
              placeholder={
                isAddingPortalFeature
                  ? "/request-pool"
                  : showAdd
                    ? "Select a document type"
                    : undefined
              }
              invalid={
                showEdit || isAddingPortalFeature
                  ? !!getResourcePathError()
                  : false
              }
              onChange={(v) => setForm({ ...form, resource_path: v })}
            />
          </FormField>

          <div style={styles.footer}>
            <button
              style={{
                ...styles.cancelBtn,
                ...(editFormLocked ? styles.cancelBtnDisabled : null),
              }}
              disabled={editFormLocked}
              onClick={() => {
                setShowAdd(false);
                setShowEdit(false);
                setSelected(null);
                resetForm();
              }}
            >
              Cancel
            </button>

            <button
              style={{
                ...styles.primaryBtn,
                ...(submitting ||
                !canSubmitResourceForm ||
                editFormLocked
                  ? styles.primaryBtnDisabled
                  : null),
              }}
              disabled={submitting || !canSubmitResourceForm || editFormLocked}
              onClick={showAdd ? handleAdd : handleEdit}
            >
              {submitting ? "Saving..." : showAdd ? "Create" : "Update"}
            </button>
          </div>
        </Modal>
      )}

      {showDeactivate && selected && (
        <Modal
          title={
            isPortalFeatureResource(selected)
              ? "Disable Portal Feature"
              : "Disable Resource"
          }
          onClose={closeDeactivateModal}
        >
          <p style={styles.confirmText}>
            Disable{" "}
            <strong>
              {selected.resource_name || selected.resource_code}
            </strong>
            {selected.resource_code && selected.resource_name
              ? ` (${selected.resource_code})`
              : null}
            ? {getResourceDeactivateMessage(selected)}
          </p>

          <div style={styles.footer}>
            <button style={styles.cancelBtn} onClick={closeDeactivateModal}>
              Cancel
            </button>

            <button
              style={{ ...styles.dangerBtn, marginTop: 0 }}
              disabled={submitting}
              onClick={confirmDeactivate}
            >
              {submitting ? "Disabling..." : "Disable"}
            </button>
          </div>
        </Modal>
      )}

      {toast && (
        <div
          style={{
            ...styles.toast,
            background: toast.type === "error" ? "#ef4444" : "#16a34a",
          }}
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
}
/* COMPONENTS */

const FormField = ({ label, required, hint, hintOnHover, error, children }) => {
  const [hintVisible, setHintVisible] = useState(false);
  const showGuidance = hintOnHover && hintVisible && !error;

  let fieldControl = children;
  if (hintOnHover && isValidElement(children)) {
    fieldControl = cloneElement(children, {
      onFocus: (...args) => {
        setHintVisible(true);
        children.props.onFocus?.(...args);
      },
      onBlur: (...args) => {
        setHintVisible(false);
        children.props.onBlur?.(...args);
      },
    });
  }

  return (
    <div
      style={styles.field}
      onMouseEnter={() => hintOnHover && setHintVisible(true)}
      onMouseLeave={() => hintOnHover && setHintVisible(false)}
    >
      <label style={styles.label}>
        {label}
        {required ? <span style={styles.requiredMark}> *</span> : null}
      </label>

      {fieldControl}

      {error ? (
        <Error msg={error} />
      ) : showGuidance ? (
        <p style={styles.hint}>{hint}</p>
      ) : null}
    </div>
  );
};

const Input = ({
  id,
  value,
  onChange,
  onBlur,
  onFocus,
  title,
  invalid,
  placeholder,
  disabled = false,
}) => (
  <input
    id={id}
    style={{
      ...styles.input,
      ...(invalid ? styles.inputInvalid : null),
      ...(disabled ? styles.inputDisabled : null),
    }}
    value={value}
    placeholder={placeholder}
    title={title}
    disabled={disabled}
    readOnly={disabled}
    onChange={(e) => onChange(e.target.value)}
    onFocus={onFocus}
    onBlur={onBlur}
  />
);

const Error = ({ msg }) => <div style={styles.error}>{msg}</div>;

const ActionTooltip = ({ text, children }) => {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [tooltipPos, setTooltipPos] = useState(null);
  const tooltipTouchTimer = useRef(null);
  const tooltipId = useRef(
    `action-tooltip-${Math.random().toString(36).slice(2, 9)}`,
  ).current;

  const showTooltip = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltipPos({
      top: rect.bottom + 8,
      left: rect.left + rect.width / 2,
    });
    setIsTooltipVisible(true);
  };

  const hideTooltip = () => {
    setIsTooltipVisible(false);
    setTooltipPos(null);
  };

  const handleTooltipTouchStart = (event) => {
    if (tooltipTouchTimer.current) {
      clearTimeout(tooltipTouchTimer.current);
    }
    showTooltip(event);
  };

  const handleTooltipTouchEnd = () => {
    tooltipTouchTimer.current = setTimeout(() => {
      hideTooltip();
    }, 1200);
  };

  useEffect(() => {
    if (!isTooltipVisible) {
      return undefined;
    }

    const handleDismiss = () => hideTooltip();

    window.addEventListener("scroll", handleDismiss, true);
    window.addEventListener("resize", handleDismiss);

    return () => {
      window.removeEventListener("scroll", handleDismiss, true);
      window.removeEventListener("resize", handleDismiss);
    };
  }, [isTooltipVisible]);

  if (!isValidElement(children)) {
    return children;
  }

  const trigger = cloneElement(children, {
    onMouseEnter: (event) => {
      children.props.onMouseEnter?.(event);
      showTooltip(event);
    },
    onMouseLeave: (event) => {
      children.props.onMouseLeave?.(event);
      hideTooltip();
    },
    onFocus: (event) => {
      children.props.onFocus?.(event);
      showTooltip(event);
    },
    onBlur: (event) => {
      children.props.onBlur?.(event);
      hideTooltip();
    },
    onTouchStart: (event) => {
      children.props.onTouchStart?.(event);
      handleTooltipTouchStart(event);
    },
    onTouchEnd: (event) => {
      children.props.onTouchEnd?.(event);
      handleTooltipTouchEnd();
    },
    "aria-describedby": isTooltipVisible
      ? tooltipId
      : children.props["aria-describedby"],
  });

  return (
    <>
      {trigger}

      {isTooltipVisible &&
        tooltipPos &&
        createPortal(
          <div
            id={tooltipId}
            style={{
              ...styles.actionTooltip,
              position: "fixed",
              top: tooltipPos.top,
              left: tooltipPos.left,
              transform: "translateX(-50%)",
            }}
            role="tooltip"
          >
            {text}
            <span style={styles.actionTooltipArrow} aria-hidden />
          </div>,
          document.body,
        )}
    </>
  );
};

const StatusToggle = ({ checked, disabled, onChange, ariaLabel, tooltipText }) => (
  <ActionTooltip text={tooltipText}>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      style={{
        ...styles.toggleButton,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
      }}
      onClick={() => {
        if (!disabled) {
          onChange(!checked);
        }
      }}
    >
      <span
        aria-hidden="true"
        style={{
          ...styles.switch,
          background: checked ? "#0f172a" : "#e2e8f0",
        }}
      >
        <span
          style={{
            ...styles.knob,
            left: checked ? "22px" : "2px",
          }}
        />
      </span>
    </button>
  </ActionTooltip>
);

const Modal = ({ title, children, onClose, headerActions }) => (
  <div style={styles.overlay}>
    <div style={styles.modal}>
      <div style={styles.modalHeader}>
        <h3>{title}</h3>

        <div style={styles.modalHeaderActions}>
          {headerActions}

          <button
            type="button"
            style={styles.close}
            aria-label="Close"
            onClick={onClose}
          >
            ✕
          </button>
        </div>
      </div>

      {children}
    </div>
  </div>
);

const styles = {
  container: {
    background: "#f8fafc",
    minHeight: "80vh",
  },

  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "24px",
    gap: "20px",
    flexWrap: "wrap",
  },

  title: {
    margin: 0,
    fontSize: "28px",
    fontWeight: "700",
    color: "#0f172a",
  },

  sub: {
    color: "#64748b",
    marginTop: "8px",
    fontSize: "14px",
  },

  addBtn: {
    background: "#0f172a",
    color: "#fff",
    border: "none",
    borderRadius: "10px",
    padding: "10px 16px",
    fontWeight: "600",
    fontSize: "14px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
    flexShrink: 0,
  },

  headerNarrow: {
    flexWrap: "nowrap",
    alignItems: "flex-start",
  },

  headerTitleBlock: {
    flex: "1 1 0",
    minWidth: 0,
  },

  addBtnCompact: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "10px",
    minWidth: "40px",
    minHeight: "40px",
  },

  addBtnWrap: {
    position: "relative",
    flexShrink: 0,
  },

  addTooltip: {
    position: "absolute",
    top: "calc(100% + 8px)",
    right: 0,
    background: "#0f172a",
    color: "#f8fafc",
    border: "1px solid #1e293b",
    borderRadius: "8px",
    padding: "6px 10px",
    fontSize: "12px",
    fontWeight: "600",
    lineHeight: 1.35,
    whiteSpace: "nowrap",
    boxShadow: "0 8px 20px rgba(0, 0, 0, 0.16)",
    zIndex: 20,
    pointerEvents: "none",
  },

  addTooltipArrow: {
    position: "absolute",
    top: "-6px",
    right: "14px",
    width: 0,
    height: 0,
    borderLeft: "6px solid transparent",
    borderRight: "6px solid transparent",
    borderBottom: "6px solid #0f172a",
  },

  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "20px",
    flexWrap: "nowrap",
    background: "#fff",
    padding: "16px",
    borderRadius: "14px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
  },

  search: {
    flex: "1 1 0",
    minWidth: 0,
    boxSizing: "border-box",
    padding: "12px 14px",
    borderRadius: "10px",
    border: "1px solid #dbe2ea",
    fontSize: "14px",
    outline: "none",
    transition: "all 0.2s ease",
    background: "#fff",
  },

  count: {
    fontSize: "13px",
    color: "#64748b",
    marginLeft: "auto",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },

  toolbarNarrow: {
    flexWrap: "wrap",
  },

  searchNarrow: {
    flex: "1 1 100%",
    width: "100%",
  },

  countNarrow: {
    flex: "1 1 100%",
    width: "100%",
    marginLeft: 0,
    textAlign: "center",
  },

  tableCard: {
    background: "#fff",
    borderRadius: "16px",
    border: "1px solid #e2e8f0",
    overflow: "hidden",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
  },

  th: {
    padding: "16px",
    textAlign: "left",
    fontSize: "12px",
    fontWeight: "700",
    color: "#64748b",
    background: "#f8fafc",
    borderBottom: "1px solid #e2e8f0",
  },

  thRight: {
    padding: "16px",
    textAlign: "right",
    fontSize: "12px",
    fontWeight: "700",
    color: "#64748b",
    background: "#f8fafc",
    borderBottom: "1px solid #e2e8f0",
  },

  row: {
    borderBottom: "1px solid #f1f5f9",
  },

  disabledPortalFeatureRow: {
    background: "#f1f5f9",
  },

  tdMuted: {
    color: "#94a3b8",
  },

  nameMuted: {
    color: "#64748b",
  },

  badgeMuted: {
    opacity: 0.72,
  },

  td: {
    padding: "16px",
    fontSize: "14px",
    color: "#0f172a",
  },

  tdRight: {
    padding: "16px",
    textAlign: "right",
    verticalAlign: "middle",
  },

  codeBadge: {
    display: "inline-block",
    background: "#f1f5f9",
    padding: "4px 8px",
    borderRadius: "6px",
    fontSize: "12px",
  },

  typeBadge: {
    display: "inline-flex",
    alignItems: "center",
    background: "#e2e8f0",
    color: "#334155",
    padding: "4px 10px",
    borderRadius: "999px",
    fontSize: "12px",
    fontWeight: "600",
  },

  accessRead: {
    display: "inline-flex",
    background: "#dbeafe",
    color: "#1d4ed8",
    padding: "4px 10px",
    borderRadius: "999px",
    fontSize: "12px",
    fontWeight: "600",
  },

  accessWrite: {
    display: "inline-flex",
    background: "#dcfce7",
    color: "#15803d",
    padding: "4px 10px",
    borderRadius: "999px",
    fontSize: "12px",
    fontWeight: "600",
  },

  actionColumn: {
    display: "inline-flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    minWidth: "42px",
  },

  iconAction: {
    cursor: "pointer",
    fontSize: "16px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: "none",
    background: "transparent",
    padding: 0,
  },

  toggleButton: {
    border: "none",
    background: "transparent",
    padding: 0,
    display: "inline-flex",
    alignItems: "center",
  },

  actionTooltip: {
    background: "#0f172a",
    color: "#f8fafc",
    border: "1px solid #1e293b",
    borderRadius: "8px",
    padding: "6px 10px",
    fontSize: "12px",
    fontWeight: "600",
    lineHeight: 1.35,
    whiteSpace: "nowrap",
    boxShadow: "0 8px 20px rgba(0, 0, 0, 0.16)",
    zIndex: 1000,
    pointerEvents: "none",
  },

  actionTooltipArrow: {
    position: "absolute",
    top: "-6px",
    left: "50%",
    transform: "translateX(-50%)",
    width: 0,
    height: 0,
    borderLeft: "6px solid transparent",
    borderRight: "6px solid transparent",
    borderBottom: "6px solid #0f172a",
  },

  switch: {
    width: "42px",
    height: "22px",
    borderRadius: "999px",
    position: "relative",
    display: "inline-block",
    overflow: "hidden",
    flexShrink: 0,
    boxSizing: "border-box",
    transition: "background 0.2s ease",
  },

  knob: {
    position: "absolute",
    top: "2px",
    left: "2px",
    width: "18px",
    height: "18px",
    borderRadius: "50%",
    background: "#fff",
    transition: "left 0.2s ease",
    boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
  },

  actionRow: {
    display: "flex",
    gap: "8px",
    justifyContent: "flex-end",
    flexWrap: "wrap",
  },

  editBtn: {
    background: "#0f172a",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    padding: "8px 12px",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: "600",
  },

  deactivateBtn: {
    background: "#fee2e2",
    color: "#dc2626",
    border: "none",
    borderRadius: "8px",
    padding: "8px 12px",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: "600",
  },

  activateBtn: {
    background: "#dcfce7",
    color: "#15803d",
    border: "none",
    borderRadius: "8px",
    padding: "8px 12px",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: "600",
  },

  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,0.55)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 999,
  },

  modal: {
    background: "#fff",
    borderRadius: "18px",
    width: "100%",
    maxWidth: "520px",
    padding: "24px",
  },

  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
  },

  modalHeaderActions: {
    display: "inline-flex",
    alignItems: "center",
    gap: "12px",
  },

  close: {
    cursor: "pointer",
    fontSize: "18px",
    color: "#64748b",
    border: "none",
    background: "transparent",
    padding: 0,
    lineHeight: 1,
  },

  label: {
    display: "block",
    fontSize: "13px",
    fontWeight: "600",
    color: "#334155",
    marginBottom: "6px",
    marginTop: "14px",
  },

  requiredMark: {
    color: "#dc2626",
    fontWeight: "700",
  },

  field: {
    marginBottom: "4px",
  },

  hint: {
    color: "#64748b",
    fontSize: "12px",
    marginTop: "6px",
    marginBottom: 0,
    lineHeight: 1.45,
  },

  input: {
    width: "100%",
    padding: "12px 14px",
    borderRadius: "10px",
    border: "1px solid #dbe2ea",
    fontSize: "14px",
    boxSizing: "border-box",
    outline: "none",
  },

  inputInvalid: {
    border: "1px solid #dc2626",
    background: "#fef2f2",
  },

  inputDisabled: {
    background: "#f1f5f9",
    color: "#64748b",
    cursor: "not-allowed",
  },

  footer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    marginTop: "28px",
  },

  cancelBtn: {
    border: "1px solid #dbe2ea",
    background: "#fff",
    padding: "10px 16px",
    borderRadius: "10px",
    cursor: "pointer",
  },

  cancelBtnDisabled: {
    opacity: 0.55,
    cursor: "not-allowed",
    background: "#f8fafc",
    color: "#94a3b8",
  },

  primaryBtn: {
    background: "#0f172a",
    color: "#fff",
    border: "none",
    padding: "10px 18px",
    borderRadius: "10px",
    cursor: "pointer",
  },

  primaryBtnDisabled: {
    opacity: 0.55,
    cursor: "not-allowed",
  },

  dangerBtn: {
    background: "#dc2626",
    color: "#fff",
    border: "none",
    padding: "10px 18px",
    borderRadius: "10px",
    cursor: "pointer",
    marginTop: "20px",
  },

  confirmText: {
    margin: 0,
    color: "#475569",
    fontSize: "14px",
    lineHeight: 1.6,
  },

  toast: {
    position: "fixed",
    bottom: "24px",
    right: "24px",
    color: "#fff",
    padding: "14px 18px",
    borderRadius: "12px",
    fontWeight: "500",
    zIndex: 1100,
    boxShadow: "0 10px 30px rgba(15, 23, 42, 0.2)",
  },

  error: {
    color: "#dc2626",
    fontSize: "12px",
    marginTop: "6px",
  },

  empty: {
    padding: "40px",
    textAlign: "center",
    color: "#64748b",
  },

  pagination: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px",
    borderTop: "1px solid #e2e8f0",
  },

  pageBtn: {
    padding: "8px 14px",
    borderRadius: "8px",
    border: "1px solid #dbe2ea",
    background: "#fff",
    fontWeight: "500",
  },

  pageText: {
    fontSize: "14px",
    color: "#475569",
  },
};
